import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Star, Phone, Globe, MapPin, DollarSign, ChevronDown, ChevronUp,
  Shield, Heart, Home, Car, Flower2, Scale, Building2, ExternalLink, Search
} from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';

// Sample trusted providers data - in production, this would come from an API
const trustedProviders = {
  insurance: [
    {
      id: 'ins-1',
      name: 'State Farm Insurance',
      type: 'Full Service Insurance',
      description: 'Comprehensive coverage options for life, home, and auto insurance with local agents.',
      website: 'https://www.statefarm.com',
      phone: '(800) 782-8332',
      location: 'Nationwide - Local agents available',
      rating: 4.7,
      reviewCount: 12500,
      priceRange: '$50 - $500/month',
      services: ['Life Insurance', 'Home Insurance', 'Auto Insurance', 'Umbrella Policies'],
      highlights: ['24/7 Claims Service', 'Local Agent Network', 'Bundle Discounts']
    },
    {
      id: 'ins-2',
      name: 'Allstate Insurance',
      type: 'Multi-Line Insurance',
      description: 'Protection for your home, car, and life with innovative digital tools.',
      website: 'https://www.allstate.com',
      phone: '(800) 255-7828',
      location: 'Nationwide',
      rating: 4.5,
      reviewCount: 9800,
      priceRange: '$45 - $450/month',
      services: ['Life Insurance', 'Home Insurance', 'Auto Insurance', 'Renters Insurance'],
      highlights: ['Drivewise® Program', 'Safe Driver Bonus', 'New Car Replacement']
    },
    {
      id: 'ins-3',
      name: 'Northwestern Mutual',
      type: 'Life Insurance Specialist',
      description: 'Focus on life insurance and financial planning with personalized service.',
      website: 'https://www.northwesternmutual.com',
      phone: '(866) 950-4644',
      location: 'Nationwide',
      rating: 4.8,
      reviewCount: 6200,
      priceRange: '$30 - $800/month',
      services: ['Term Life', 'Whole Life', 'Universal Life', 'Estate Planning'],
      highlights: ['Financial Planning', 'High Ratings', 'Dividend Payments']
    },
    {
      id: 'ins-4',
      name: 'Lemonade Insurance',
      type: 'Digital Insurance',
      description: 'Modern, AI-powered insurance with instant quotes and fast claims.',
      website: 'https://www.lemonade.com',
      phone: '(844) 733-8666',
      location: 'Online - Most US States',
      rating: 4.6,
      reviewCount: 4500,
      priceRange: '$25 - $200/month',
      services: ['Renters Insurance', 'Home Insurance', 'Pet Insurance', 'Life Insurance'],
      highlights: ['Instant Quotes', 'App-Based', 'Giveback Program']
    }
  ],
  funeral: [
    {
      id: 'fun-1',
      name: 'Dignity Memorial',
      type: 'Full Service Funeral Provider',
      description: 'North America\'s largest network of funeral, cremation, and cemetery providers.',
      website: 'https://www.dignitymemorial.com',
      phone: '(866) 564-0062',
      location: 'Nationwide - 2,000+ locations',
      rating: 4.6,
      reviewCount: 8900,
      priceRange: '$2,000 - $15,000',
      services: ['Traditional Funerals', 'Cremation', 'Memorial Services', 'Pre-Planning'],
      highlights: ['Pre-Need Planning', 'National Transferability', 'Grief Support']
    },
    {
      id: 'fun-2',
      name: 'Neptune Society',
      type: 'Cremation Services',
      description: 'America\'s largest provider of affordable cremation services.',
      website: 'https://www.neptunesociety.com',
      phone: '(800) 637-8863',
      location: 'Nationwide',
      rating: 4.4,
      reviewCount: 5600,
      priceRange: '$1,000 - $4,000',
      services: ['Direct Cremation', 'Memorial Services', 'Sea Scattering', 'Pre-Planning'],
      highlights: ['Affordable Options', 'Pre-Planning Available', 'Sea Services']
    },
    {
      id: 'fun-3',
      name: 'Tulip Cremation',
      type: 'Online Cremation Planning',
      description: 'Simple, dignified cremation services arranged entirely online.',
      website: 'https://www.tulipcremation.com',
      phone: '(844) 942-4909',
      location: 'Select States',
      rating: 4.8,
      reviewCount: 2100,
      priceRange: '$800 - $2,500',
      services: ['Direct Cremation', 'Viewing Options', 'Urn Selection', 'Memorial Planning'],
      highlights: ['Transparent Pricing', 'Online Arrangement', '24/7 Support']
    },
    {
      id: 'fun-4',
      name: 'Forest Lawn Memorial Parks',
      type: 'Memorial Parks & Funeral Homes',
      description: 'Beautiful memorial parks offering comprehensive end-of-life services.',
      website: 'https://www.forestlawn.com',
      phone: '(800) 204-3131',
      location: 'California - Multiple Locations',
      rating: 4.7,
      reviewCount: 4200,
      priceRange: '$3,000 - $25,000',
      services: ['Burials', 'Cremation', 'Mausoleums', 'Memorial Services'],
      highlights: ['Beautiful Grounds', 'Celebrity Resting Place', 'Art Collections']
    }
  ],
  legal: [
    {
      id: 'leg-1',
      name: 'LegalZoom',
      type: 'Online Legal Services',
      description: 'Create your last will and testament online with attorney review options.',
      website: 'https://www.legalzoom.com',
      phone: '(800) 773-0888',
      location: 'Online - All 50 States',
      rating: 4.5,
      reviewCount: 15000,
      priceRange: '$89 - $299',
      services: ['Last Will', 'Living Trust', 'Power of Attorney', 'Healthcare Directive'],
      highlights: ['Affordable', 'Attorney Review Available', 'Easy Updates']
    },
    {
      id: 'leg-2',
      name: 'Trust & Will',
      type: 'Estate Planning Platform',
      description: 'Modern estate planning made simple, with state-specific documents.',
      website: 'https://trustandwill.com',
      phone: '(855) 873-2898',
      location: 'Online - All 50 States',
      rating: 4.7,
      reviewCount: 8500,
      priceRange: '$159 - $699',
      services: ['Wills', 'Trusts', 'Guardian Nominations', 'Asset Protection'],
      highlights: ['State-Specific', 'Easy Process', 'Lifetime Updates']
    },
    {
      id: 'leg-3',
      name: 'Rocket Lawyer',
      type: 'Legal Services Subscription',
      description: 'Access to legal documents and attorney consultations on demand.',
      website: 'https://www.rocketlawyer.com',
      phone: '(877) 881-0947',
      location: 'Online - All 50 States',
      rating: 4.4,
      reviewCount: 6800,
      priceRange: '$39.99/month or $249 one-time',
      services: ['Wills', 'Living Wills', 'Trusts', 'Attorney Access'],
      highlights: ['Subscription Model', 'Attorney On-Call', 'Document Library']
    },
    {
      id: 'leg-4',
      name: 'Gentreo',
      type: 'Family Estate Planning',
      description: 'Comprehensive estate planning with digital vault for document storage.',
      website: 'https://www.gentreo.com',
      phone: '(617) 804-1531',
      location: 'Online - All 50 States',
      rating: 4.6,
      reviewCount: 3200,
      priceRange: '$99 - $499',
      services: ['Wills', 'Healthcare Proxy', 'Digital Vault', 'Family Sharing'],
      highlights: ['Digital Vault', 'Family Access', 'Pet Planning']
    }
  ]
};

// Provider card component
function ProviderCard({ provider, onSelect }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <Card className="rounded-2xl border-2 border-[#EBF0F5] bg-white shadow-lg hover:shadow-xl transition-all overflow-hidden">
        <div className="p-5">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-bold text-gray-800">{provider.name}</h3>
                <a 
                  href={provider.website} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#2B4C7E] hover:text-[#1E3A63]"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
              <p className="text-xs text-[#2B4C7E] font-medium">{provider.type}</p>
            </div>
            <div className="flex items-center gap-1 bg-[#F2E9D9] px-2 py-1 rounded-full">
              <Star className="h-4 w-4 text-[#C8A97E] fill-[#C8A97E]" />
              <span className="text-sm font-bold text-[#9C7F55]">{provider.rating}</span>
              <span className="text-xs text-[#B3956B]">({provider.reviewCount.toLocaleString()})</span>
            </div>
          </div>

          {/* Description */}
          <p className="text-sm text-gray-600 mb-4">{provider.description}</p>

          {/* Quick Info */}
          <div className="grid grid-cols-2 gap-2 mb-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <DollarSign className="h-4 w-4 text-green-500" />
              <span className="font-medium">{provider.priceRange}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <MapPin className="h-4 w-4 text-[#2B4C7E]" />
              <span className="truncate">{provider.location}</span>
            </div>
          </div>

          {/* Services Tags */}
          <div className="flex flex-wrap gap-1 mb-4">
            {provider.services.slice(0, 3).map((service, idx) => (
              <span 
                key={idx}
                className="text-xs bg-[#EBF0F5] text-[#1E3A63] px-2 py-1 rounded-full"
              >
                {service}
              </span>
            ))}
            {provider.services.length > 3 && (
              <span className="text-xs text-gray-500">+{provider.services.length - 3} more</span>
            )}
          </div>

          {/* Expand/Collapse */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            className="w-full text-gray-500 hover:text-[#2B4C7E]"
          >
            {expanded ? (
              <>Less info <ChevronUp className="h-4 w-4 ml-1" /></>
            ) : (
              <>More info <ChevronDown className="h-4 w-4 ml-1" /></>
            )}
          </Button>

          {/* Expanded Details */}
          <AnimatePresence>
            {expanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="pt-4 border-t border-gray-100 mt-3 space-y-3">
                  {/* Highlights */}
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-2">HIGHLIGHTS</p>
                    <div className="flex flex-wrap gap-2">
                      {provider.highlights.map((highlight, idx) => (
                        <span 
                          key={idx}
                          className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded-full flex items-center gap-1"
                        >
                          <span className="text-green-500">✓</span>
                          {highlight}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* All Services */}
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-2">ALL SERVICES</p>
                    <div className="flex flex-wrap gap-1">
                      {provider.services.map((service, idx) => (
                        <span 
                          key={idx}
                          className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full"
                        >
                          {service}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Contact Actions */}
                  <div className="flex gap-2 pt-2">
                    <a 
                      href={provider.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1"
                    >
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full rounded-full text-[#2B4C7E] border-[#D7E0EA] hover:bg-[#EBF0F5]"
                      >
                        <Globe className="h-4 w-4 mr-1" />
                        Visit Website
                      </Button>
                    </a>
                    <a href={`tel:${provider.phone}`} className="flex-1">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full rounded-full text-green-600 border-green-200 hover:bg-green-50"
                      >
                        <Phone className="h-4 w-4 mr-1" />
                        Call Now
                      </Button>
                    </a>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </Card>
    </motion.div>
  );
}

// Google Search Section Component
function GoogleSearchSection({ category }) {
  const searchQueries = {
    insurance: [
      { label: 'Life Insurance Near Me', query: 'life insurance companies near me' },
      { label: 'Home Insurance Quotes', query: 'best home insurance quotes' },
      { label: 'Auto Insurance Rates', query: 'affordable auto insurance rates' },
      { label: 'Insurance Brokers', query: 'independent insurance brokers near me' }
    ],
    funeral: [
      { label: 'Funeral Homes Near Me', query: 'funeral homes near me' },
      { label: 'Cremation Services', query: 'cremation services near me prices' },
      { label: 'Memorial Parks', query: 'memorial parks and cemeteries near me' },
      { label: 'Pre-Planning Services', query: 'funeral pre-planning services' }
    ],
    legal: [
      { label: 'Estate Planning Attorneys', query: 'estate planning attorneys near me' },
      { label: 'Will Writing Services', query: 'will writing services near me' },
      { label: 'Trust Lawyers', query: 'trust and estate lawyers' },
      { label: 'Probate Attorneys', query: 'probate attorneys near me' }
    ]
  };

  const queries = searchQueries[category] || searchQueries.insurance;

  const categoryColors = {
    insurance: 'from-[#2B4C7E] to-[#2B4C7E]',
    funeral: 'from-[#2B4C7E] to-[#C8A97E]',
    legal: 'from-[#C8A97E] to-[#C8A97E]'
  };

  return (
    <div className="mt-6 p-4 bg-white/70 rounded-2xl border border-gray-200">
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-10 h-10 bg-gradient-to-br ${categoryColors[category] || categoryColors.insurance} rounded-xl flex items-center justify-center`}>
          <Search className="h-5 w-5 text-white" />
        </div>
        <div>
          <h4 className="font-bold text-gray-800">Search for More Options</h4>
          <p className="text-xs text-gray-500">Find additional services on Google</p>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-2">
        {queries.map((item, idx) => (
          <a
            key={idx}
            href={`https://www.google.com/search?q=${encodeURIComponent(item.query)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 p-3 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors group"
          >
            <Search className="h-4 w-4 text-gray-400 group-hover:text-[#2B4C7E] transition-colors" />
            <span className="text-sm text-gray-700 group-hover:text-[#2B4C7E] transition-colors">{item.label}</span>
            <ExternalLink className="h-3 w-3 text-gray-300 ml-auto group-hover:text-[#3A5C8F] transition-colors" />
          </a>
        ))}
      </div>
      
      {/* Custom Search */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500 mb-2">Or search for something specific:</p>
        <form 
          onSubmit={(e) => {
            e.preventDefault();
            const searchInput = e.target.querySelector('input');
            if (searchInput.value) {
              window.open(`https://www.google.com/search?q=${encodeURIComponent(searchInput.value)}`, '_blank');
            }
          }}
          className="flex gap-2"
        >
          <input
            type="text"
            placeholder={`Search ${category === 'insurance' ? 'insurance' : category === 'funeral' ? 'funeral services' : 'legal services'}...`}
            className="flex-1 px-4 py-2 bg-white border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#B9C7D6] focus:border-transparent"
          />
          <Button 
            type="submit"
            size="sm" 
            className="bg-gradient-to-r from-[#2B4C7E] to-[#2B4C7E] text-white rounded-full px-4 hover:opacity-90"
          >
            <Search className="h-4 w-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}

// Main component
export default function TrustedProviders({ category, title, description, icon: Icon }) {
  const [isOpen, setIsOpen] = useState(false);
  const providers = trustedProviders[category] || [];

  const categoryIcons = {
    insurance: Shield,
    funeral: Flower2,
    legal: Scale
  };

  const CategoryIcon = Icon || categoryIcons[category] || Building2;

  const categoryColors = {
    insurance: {
      gradient: 'from-[#2B4C7E] to-[#2B4C7E]',
      bg: 'from-[#EBF0F5] to-[#EBF0F5]',
      border: 'border-[#D7E0EA]',
      text: 'text-[#2B4C7E]'
    },
    funeral: {
      gradient: 'from-[#2B4C7E] to-[#C8A97E]',
      bg: 'from-[#EBF0F5] to-[#F7F1E8]',
      border: 'border-[#D7E0EA]',
      text: 'text-[#2B4C7E]'
    },
    legal: {
      gradient: 'from-[#C8A97E] to-[#C8A97E]',
      bg: 'from-[#F7F1E8] to-[#F7F1E8]',
      border: 'border-[#EAD9BF]',
      text: 'text-[#B3956B]'
    }
  };

  const colors = categoryColors[category] || categoryColors.insurance;

  return (
    <Card className={`rounded-2xl border-2 ${colors.border} bg-gradient-to-br ${colors.bg} shadow-lg overflow-hidden`}>
      {/* Header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-6 flex items-center justify-between hover:bg-white/30 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className={`w-14 h-14 bg-gradient-to-br ${colors.gradient} rounded-2xl flex items-center justify-center shadow-lg`}>
            <CategoryIcon className="h-7 w-7 text-white" />
          </div>
          <div className="text-left">
            <h3 className="text-lg font-bold text-gray-800">{title || 'Browse Trusted Providers'}</h3>
            <p className="text-sm text-gray-600">
              {description || `Find reputable ${category} providers in your area`}
            </p>
          </div>
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className={`w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center`}
        >
          <ChevronDown className={`h-5 w-5 ${isOpen ? colors.text : 'text-gray-400'}`} />
        </motion.div>
      </button>

      {/* Expandable Provider List */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6">
              <div className="mb-4 p-3 bg-white/50 rounded-xl">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">💡 Tip:</span> These are reputable providers we recommend. 
                  Compare prices and services to find the best fit for your needs.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {providers.map((provider) => (
                  <ProviderCard key={provider.id} provider={provider} />
                ))}
              </div>

              {/* Google Search Section */}
              <GoogleSearchSection category={category} />

              <div className="mt-4 p-3 bg-gray-100/50 rounded-xl text-center">
                <p className="text-xs text-gray-500">
                  Prices and ratings are approximate and may vary. Always verify directly with the provider.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}
